import type {CreateExtArgs, DeepFunctionMap, DmApis, Extension, ExtensionAPI} from '@wix/document-manager-core'
import type {CompRef, BuilderPresets} from '@wix/document-services-types'
import type {FeaturesExtensionAPI} from './features/features'
import {COMP_DATA_QUERY_KEYS_WITH_STYLE, DATA_TYPES} from '../constants/constants'
import type {SchemaExtensionAPI} from './schema/schema'
import {autoType} from '../utils/builderUtils'
import type {VariantsExtensionAPI} from './variants/variants'

export interface PresetsAPI extends DeepFunctionMap {
    get(compRef: CompRef): BuilderPresets
    set(compRef: CompRef, presets: BuilderPresets): void
}

export interface PresetsExtensionAPI extends ExtensionAPI {
    presets: PresetsAPI
}

const createExtension = (): Extension => {
    const createExtensionAPI = ({extensionAPI, dal}: CreateExtArgs): PresetsExtensionAPI => {
        const {features, schemaAPI, variants} = extensionAPI as FeaturesExtensionAPI &
            SchemaExtensionAPI &
            VariantsExtensionAPI

        return {
            presets: {
                get: compRef => features.component.get(compRef, DATA_TYPES.presets),
                set: (compRef, presets) => {
                    const {componentType} = dal.get(compRef)
                    const presetsType = presets.type ?? schemaAPI.getDefinition(componentType)?.builderTypes.presets
                    variants.updateDataConsideringVariants({
                        pointerWithVariants: compRef,
                        valueToUpdate: autoType(presets, presetsType),
                        namespace: DATA_TYPES.presets,
                        referenceName: COMP_DATA_QUERY_KEYS_WITH_STYLE.presets
                    })
                }
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {presets} = extensionAPI as PresetsExtensionAPI
        return {
            presets: {
                get: presets.get,
                set: presets.set
            }
        }
    }

    return {
        name: 'presets',
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
